/*!
 * Girlbasket Theme v1.0.0
 * Copyright (c) 2026 Om. All rights reserved.
 *
 * This file is part of the Girlbasket theme, licensed only for use on the
 * Girlbasket store (girlbasket.myshopify.com / girlbasket.com). Copying,
 * modifying, redistributing or reselling this file, in whole or in part,
 * without written permission from Om is prohibited. See LICENSE.md.
 *
 * File: assets/gb-sections.js
 */
(() => {
  'use strict';

  const GB = window.GB;
  if (!GB || !GB.events) return;

  /* ---------------------------------------------------------------------
     <gb-slideshow>
     --------------------------------------------------------------------- */
  class GbSlideshow extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('[data-gb-slide]')];
      this.dots = [...this.querySelectorAll('[data-gb-dot]')];
      this.index = 0;
      this.autoplay = this.dataset.autoplay === 'true' && !GB.media.reducedMotion.matches;
      this.interval = Number(this.dataset.interval) || 6000;
      if (this.slides.length < 2) return;

      this.querySelector('[data-gb-prev]')?.addEventListener('click', () => this.go(this.index - 1));
      this.querySelector('[data-gb-next]')?.addEventListener('click', () => this.go(this.index + 1));
      this.dots.forEach((dot) => dot.addEventListener('click', () => this.go(Number(dot.dataset.gbDot))));
      this.pauseButton = this.querySelector('[data-gb-pause]');
      this.pauseButton?.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        this.pauseButton.setAttribute('aria-pressed', String(this.userPaused));
        this.userPaused ? this.stop() : this.start();
      });

      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.start());
      this.addEventListener('focusin', () => this.stop());

      // Swipe on touch screens.
      let startX = null;
      this.addEventListener('touchstart', (event) => {
        startX = event.touches[0].clientX;
      }, { passive: true });
      this.addEventListener('touchend', (event) => {
        if (startX === null) return;
        const dx = event.changedTouches[0].clientX - startX;
        if (Math.abs(dx) > 40) this.go(this.index + (dx < 0 ? 1 : -1));
        startX = null;
      });

      // Pause while off screen or in a hidden tab.
      this.observer = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        this.visible ? this.start() : this.stop();
      });
      this.observer.observe(this);
      document.addEventListener('visibilitychange', () => (document.hidden ? this.stop() : this.start()));
    }

    disconnectedCallback() {
      this.stop();
      if (this.observer) this.observer.disconnect();
    }

    go(next) {
      const count = this.slides.length;
      const previous = this.index;
      this.index = ((next % count) + count) % count;
      this.slides.forEach((slide, i) => {
        const active = i === this.index;
        slide.classList.toggle('is-active', active);
        slide.classList.toggle('is-leaving', i === previous && !active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.querySelectorAll('a, button').forEach((el) => {
          el.tabIndex = active ? 0 : -1;
        });
      });
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === this.index);
        if (i === this.index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    start() {
      if (!this.autoplay || this.userPaused || this.visible === false) return;
      this.stop();
      this.timer = setInterval(() => this.go(this.index + 1), this.interval);
    }

    stop() {
      clearInterval(this.timer);
    }

    select(slide) {
      const i = this.slides.indexOf(slide);
      if (i < 0) return;
      this.stop();
      this.go(i);
    }
  }
  customElements.define('gb-slideshow', GbSlideshow);

  /* ---------------------------------------------------------------------
     <gb-hotspots>: pins that open a small card
     --------------------------------------------------------------------- */
  class GbHotspots extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (event) => {
        const pin = event.target.closest('[data-gb-hotspot]');
        if (!pin) return;
        const open = pin.getAttribute('aria-expanded') === 'true';
        this.closeAll();
        if (!open) this.open(pin);
      });
      this.onDocumentClick = (event) => {
        if (!this.contains(event.target)) this.closeAll();
      };
      document.addEventListener('click', this.onDocumentClick);
      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        const pin = this.querySelector('[data-gb-hotspot][aria-expanded="true"]');
        this.closeAll();
        if (pin) pin.focus();
      });
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
    }

    open(pin) {
      pin.setAttribute('aria-expanded', 'true');
      const card = document.getElementById(pin.getAttribute('aria-controls'));
      if (card) card.hidden = false;
      pin.closest('.gb-hotspot').classList.add('is-open');
    }

    closeAll() {
      this.querySelectorAll('[data-gb-hotspot]').forEach((pin) => {
        pin.setAttribute('aria-expanded', 'false');
        const card = document.getElementById(pin.getAttribute('aria-controls'));
        if (card) card.hidden = true;
        pin.closest('.gb-hotspot').classList.remove('is-open');
      });
    }
  }
  customElements.define('gb-hotspots', GbHotspots);

  /* ---------------------------------------------------------------------
     <gb-shop-look>: pins on the image highlight their product card
     --------------------------------------------------------------------- */
  class GbShopLook extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (event) => {
        const pin = event.target.closest('[data-gb-look-pin]');
        if (!pin) return;
        const index = pin.dataset.gbLookPin;
        const item = this.querySelector(`[data-gb-look-item="${index}"]`);
        const carousel = this.querySelector('gb-carousel');
        this.querySelectorAll('.gb-look__item, .gb-look__pin').forEach((el) => el.classList.remove('is-active'));
        pin.classList.add('is-active');
        if (item) {
          item.classList.add('is-active');
          if (carousel && carousel.goTo) carousel.goTo([...item.parentElement.children].indexOf(item));
        }
      });
    }
  }
  customElements.define('gb-shop-look', GbShopLook);

  /* ---------------------------------------------------------------------
     <gb-faq-search>: filters FAQ questions and lists the topics
     --------------------------------------------------------------------- */
  class GbFaqSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-gb-faq-input]');
      this.empty = this.querySelector('[data-gb-faq-empty]');
      const index = this.querySelector('[data-gb-faq-index]');

      if (index) {
        const sections = [...document.querySelectorAll('[data-gb-faq]')];
        sections.forEach((section) => {
          const title = section.dataset.gbFaqTitle;
          if (!title) return;
          const link = document.createElement('a');
          link.href = `#${section.id}`;
          link.className = 'gb-chip';
          link.textContent = title;
          index.appendChild(link);
        });
        if (!index.children.length) index.hidden = true;
      }

      if (this.input) this.input.addEventListener('input', GB.debounce(() => this.filter(), 150));
    }

    filter() {
      const query = this.input.value.trim().toLowerCase();
      let matches = 0;
      document.querySelectorAll('[data-gb-faq]').forEach((section) => {
        let sectionMatches = 0;
        section.querySelectorAll('.gb-faq__item').forEach((item) => {
          const hit = !query || item.textContent.toLowerCase().includes(query);
          item.hidden = !hit;
          if (hit) sectionMatches += 1;
          if (query && hit) item.open = true;
        });
        section.hidden = sectionMatches === 0;
        matches += sectionMatches;
      });
      if (this.empty) this.empty.hidden = matches > 0;
    }
  }
  customElements.define('gb-faq-search', GbFaqSearch);

  /* ---------------------------------------------------------------------
     <gb-popup>: newsletter / promo pop-up
     --------------------------------------------------------------------- */
  class GbPopup extends HTMLElement {
    connectedCallback() {
      this.key = `gb-popup-${this.dataset.id}`;
      this.panel = this.querySelector('.gb-popup__panel');
      this.querySelectorAll('[data-gb-popup-close]').forEach((el) => el.addEventListener('click', () => this.close()));
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.close();
      });

      if (this.hasAttribute('data-design-mode')) return;
      if (this.querySelector('[data-gb-popup-success]') || this.querySelector('.gb-form-message--error')) {
        this.open();
        return;
      }
      if (this.dataset.mobile === 'false' && !GB.media.tablet.matches) return;
      if (this.wasDismissed()) return;

      const trigger = this.dataset.trigger;
      if (trigger === 'delay' || trigger === 'both') {
        this.timer = setTimeout(() => this.open(), (Number(this.dataset.delay) || 8) * 1000);
      }
      if ((trigger === 'exit' || trigger === 'both') && GB.media.hover.matches) {
        this.onLeave = (event) => {
          if (event.clientY <= 0) this.open();
        };
        document.documentElement.addEventListener('mouseleave', this.onLeave);
      }
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      if (this.onLeave) document.documentElement.removeEventListener('mouseleave', this.onLeave);
    }

    wasDismissed() {
      try {
        const stamp = Number(localStorage.getItem(this.key));
        const days = Number(this.dataset.days) || 7;
        return stamp && Date.now() - stamp < days * 86400000;
      } catch (error) {
        return false;
      }
    }

    open() {
      if (this.isOpen) return;
      this.isOpen = true;
      clearTimeout(this.timer);
      if (this.onLeave) document.documentElement.removeEventListener('mouseleave', this.onLeave);
      this.hidden = false;
      requestAnimationFrame(() => this.classList.add('is-open'));
      this.opener = document.activeElement;
      if (this.classList.contains('gb-popup--center')) {
        GB.lockScroll();
        this.release = GB.trapFocus(this.panel);
      }
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.classList.remove('is-open');
      if (this.release) {
        this.release();
        this.release = null;
        GB.unlockScroll();
      }
      setTimeout(() => {
        this.hidden = true;
      }, 300);
      try {
        localStorage.setItem(this.key, String(Date.now()));
      } catch (error) {
        /* Storage blocked: the pop-up may show again. */
      }
      if (this.opener && this.opener.focus) this.opener.focus({ preventScroll: true });
    }
  }
  customElements.define('gb-popup', GbPopup);

  /* ---------------------------------------------------------------------
     <gb-map>: loads the map only when it scrolls into view
     --------------------------------------------------------------------- */
  const MAP_STYLES = {
    silver: [
      { elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
      { elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
      { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
      { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#c9c9c9' }] }
    ],
    retro: [
      { elementType: 'geometry', stylers: [{ color: '#ebe3cd' }] },
      { elementType: 'labels.text.fill', stylers: [{ color: '#523735' }] },
      { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#f5f1e6' }] },
      { featureType: 'water', elementType: 'geometry.fill', stylers: [{ color: '#b9d3c2' }] }
    ],
    dark: [
      { elementType: 'geometry', stylers: [{ color: '#212121' }] },
      { elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
      { featureType: 'road', elementType: 'geometry.fill', stylers: [{ color: '#2c2c2c' }] },
      { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#000000' }] }
    ],
    night: [
      { elementType: 'geometry', stylers: [{ color: '#242f3e' }] },
      { elementType: 'labels.text.fill', stylers: [{ color: '#746855' }] },
      { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#38414e' }] },
      { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#17263c' }] }
    ],
    minimal: [
      { featureType: 'poi', stylers: [{ visibility: 'off' }] },
      { featureType: 'transit', stylers: [{ visibility: 'off' }] },
      { elementType: 'geometry', stylers: [{ saturation: -100 }] }
    ],
    standard: []
  };

  class GbMap extends HTMLElement {
    connectedCallback() {
      this.observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting) return;
          this.observer.disconnect();
          this.load();
        },
        { rootMargin: '200px' }
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    load() {
      if (this.dataset.mode === 'api' && this.dataset.key) this.loadApi();
      else this.loadEmbed();
    }

    loadEmbed() {
      const iframe = document.createElement('iframe');
      iframe.src = `https://maps.google.com/maps?q=${this.dataset.query}&z=${this.dataset.zoom || 15}&output=embed`;
      iframe.title = this.dataset.title || this.dataset.address || 'Map';
      iframe.loading = 'lazy';
      iframe.referrerPolicy = 'no-referrer-when-downgrade';
      this.replaceChildren(iframe);
    }

    loadApi() {
      const callback = `gbMapReady${Math.random().toString(36).slice(2)}`;
      window[callback] = () => this.render();
      if (window.google && window.google.maps) {
        this.render();
        return;
      }
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(this.dataset.key)}&callback=${callback}`;
      script.async = true;
      script.onerror = () => this.loadEmbed();
      document.head.appendChild(script);
    }

    render() {
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ address: this.dataset.address }, (results, status) => {
        if (status !== 'OK' || !results[0]) {
          this.loadEmbed();
          return;
        }
        const container = document.createElement('div');
        container.className = 'gb-map__canvas';
        this.replaceChildren(container);
        const location = results[0].geometry.location;
        const map = new google.maps.Map(container, {
          center: location,
          zoom: Number(this.dataset.zoom) || 15,
          styles: MAP_STYLES[this.dataset.style] || [],
          disableDefaultUI: true,
          zoomControl: true
        });
        new google.maps.Marker({ map, position: location, title: this.dataset.title });
      });
    }
  }
  customElements.define('gb-map', GbMap);

  /* ---------------------------------------------------------------------
     <gb-size-guide>: tabs and the cm / inches switch
     --------------------------------------------------------------------- */
  class GbSizeGuide extends HTMLElement {
    connectedCallback() {
      this.tabs = [...this.querySelectorAll('[role="tab"]')];
      this.panels = [...this.querySelectorAll('[role="tabpanel"]')];
      if (this.tabs.length) {
        this.select(this.tabs[0]);
        this.tabs.forEach((tab, i) => {
          tab.addEventListener('click', () => this.select(tab));
          tab.addEventListener('keydown', (event) => {
            if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
            const next = this.tabs[(i + (event.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length];
            next.focus();
            this.select(next);
          });
        });
      }

      this.querySelectorAll('[data-gb-unit]').forEach((button) =>
        button.addEventListener('click', () => this.setUnit(button.dataset.gbUnit))
      );
    }

    select(tab) {
      this.tabs.forEach((other) => {
        const active = other === tab;
        other.setAttribute('aria-selected', String(active));
        other.tabIndex = active ? 0 : -1;
      });
      this.panels.forEach((panel) => {
        panel.hidden = panel.id !== tab.getAttribute('aria-controls');
      });
    }

    setUnit(unit) {
      this.querySelectorAll('[data-gb-unit]').forEach((button) => {
        const active = button.dataset.gbUnit === unit;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
      this.querySelectorAll('[data-gb-convertible] [data-gb-cm]').forEach((cell) => {
        const original = cell.dataset.gbCm;
        cell.textContent =
          unit === 'in'
            ? original.replace(/\d+(?:[.,]\d+)?/g, (match) => (parseFloat(match.replace(',', '.')) / 2.54).toFixed(1))
            : original;
      });
    }
  }
  customElements.define('gb-size-guide', GbSizeGuide);

  /* ---------------------------------------------------------------------
     Theme editor: show the selected slide or open the selected pop-up
     --------------------------------------------------------------------- */
  document.addEventListener('shopify:block:select', (event) => {
    const slideshow = event.target.closest('gb-slideshow');
    if (slideshow) slideshow.select(event.target);
  });
  document.addEventListener('shopify:section:select', (event) => {
    const popup = event.target.querySelector('gb-popup');
    if (popup) popup.open();
  });
  document.addEventListener('shopify:section:deselect', (event) => {
    const popup = event.target.querySelector('gb-popup');
    if (popup) popup.close();
  });
})();
